/**
 * ADP mapping profile — the CONFIGURABLE column-mapping shape behind the ADP
 * paydata export (Time Evaluation Phase 4).
 *
 * WHY THIS EXISTS: an ADP Workforce Now "paydata" grid is configured PER COMPANY.
 * Two clients on the same WFN release can have different column headers, a
 * different number of Hours/Earnings slots, and completely different earnings
 * codes. Hard-coding a header row or an earnings code produces a file the
 * client's own ADP instance rejects. So the column names, the slot counts and
 * the internal-code -> ADP-code map are DATA (an `AdpMappingProfile` persisted in
 * `payroll_export_profiles`), and the serializer is generic over that data.
 *
 * This module is PURE — no I/O, no DOM, no Supabase. It owns:
 *   • the profile + column shapes (`AdpMappingProfile`, `AdpColumnMapping`)
 *   • the resolution helpers the serializer calls (`resolveCoCode`,
 *     `renderBatchId`, `mapEarningsCode`, `formatAdpCell`)
 *   • config-time validation (`validateAdpProfile`)
 *   • the "paste your own header row" re-binder (`parseAdpHeaderRow`)
 *
 * The shipped presets live next door in `adpPaydataPreset.ts`; the serializer
 * that consumes all of this is `payroll/serializeAdpPaydata.ts`.
 */

import { normalizeEpipLayout, validateEpipLayout, type AdpEpipLayout } from './adpEpipLayout'

// ─── Field + source model ──────────────────────────────────────────────────────

/**
 * A scalar that comes off the export row itself rather than out of the earnings
 * lines. `coCode` / `batchId` / `fileNumber` are the three ADP identity columns;
 * the rest are optional passthroughs a client's template may or may not carry.
 */
export type AdpMappableField =
  | 'coCode'
  | 'batchId'
  | 'fileNumber'
  | 'workDate'
  | 'periodEnd'
  | 'employeeName'
  | 'tempDept'
  | 'tempRate'
  | 'positionId'
  | 'payrollTenant'

/** Cell formatting. ADP is strict about 2dp hours/money and MM/DD/YYYY dates. */
export type AdpColumnFormat = 'hours2' | 'money2' | 'date_mmddyyyy' | 'text'

/**
 * Where a column's value comes from.
 *
 * `earningsHours` / `earningsAmount` are FIXED bindings: the column always
 * carries the named internal earnings codes (that is how `Reg Hours` and
 * `O/T Hours` work — a dedicated column per code).
 *
 * `hoursSlot*` / `earningsSlot*` are DYNAMIC bindings: a Code/Amount pair whose
 * code is chosen at serialize time from whatever premium lines the worker
 * actually has. That is how a 3rd-shift differential reaches ADP.
 */
export type AdpColumnSource =
  | { kind: 'literal'; value: string }
  | { kind: 'field'; field: AdpMappableField }
  | { kind: 'earningsHours'; earningsCodes: string[] }
  | { kind: 'earningsAmount'; earningsCodes: string[] }
  | { kind: 'hoursSlotCode'; slot: number }
  | { kind: 'hoursSlotAmount'; slot: number }
  | { kind: 'earningsSlotCode'; slot: number }
  | { kind: 'earningsSlotAmount'; slot: number }
  | { kind: 'blank' }

export interface AdpColumnMapping {
  /** The LITERAL header text ADP expects, e.g. 'O/T Hours'. Client-editable. */
  column: string
  source: AdpColumnSource
  format?: AdpColumnFormat
}

export interface AdpMappingProfile {
  id: string
  name: string
  /**
   * `wfn_paydata_csv` / `generic_csv` are column grids (serializeAdpPaydata).
   * `adp_epip` is the record-typed HDR / DET / TRL payroll input file
   * (serializeAdpEpip); it reads `epip` and ignores `columns` and the slots.
   */
  vendorFormat: 'wfn_paydata_csv' | 'generic_csv' | 'adp_epip'
  /**
   * payroll tenant -> ADP 3-char Co Code.
   * Key `''` (ADP_DEFAULT_TENANT_KEY) is the untagged / single-tenant entry —
   * `PayrollBatchLine.payrollTenant` is null for a single-tenant customer.
   * Key `'*'` (ADP_WILDCARD_TENANT_KEY) is a catch-all fallback.
   */
  companyCodeByTenant: Record<string, string>
  /** Token template, e.g. 'TS{PERIOD_END:MMDD}'. See `renderBatchId`. */
  batchIdTemplate: string
  /** THE client-specific bit: internal earnings code -> ADP earnings code. */
  earningsCodeMap: Record<string, string>
  regularCodes: string[]
  overtimeCodes: string[]
  columns: AdpColumnMapping[]
  /** COUNT of Hours Code/Amount slot pairs. Ordinals run 3 .. 3+hoursSlots-1. */
  hoursSlots: number
  /** COUNT of Earnings Code/Amount slot pairs. Ordinals run 3 .. 3+earningsSlots-1. */
  earningsSlots: number
  slotOverflow: 'spill_extra_row' | 'block'
  rowGranularity: 'period' | 'day'
  includeHeaderRow: boolean
  lineEnding: 'crlf' | 'lf'
  /** What a null/absent value serializes to. Usually '' (a bare comma). */
  emptyValue: string
  /**
   * The EPIP record layout. Required when `vendorFormat` is `'adp_epip'`,
   * ignored otherwise. See `adpEpipLayout.ts`.
   */
  epip?: AdpEpipLayout
}

// ─── Constants the serializer must share ───────────────────────────────────────

/**
 * ADP's Hours/Earnings slot ordinals START AT 3, because slots 1 and 2 are
 * spoken for by the dedicated `Reg Hours` / `O/T Hours` (and `Reg Earnings` /
 * `O/T Earnings`) columns. So a profile with `hoursSlots: 2` has ordinals 3 and 4.
 * Get this wrong and every premium lands one column to the left.
 */
export const ADP_FIRST_SLOT_ORDINAL = 3

/** The `companyCodeByTenant` key for a null/untagged payroll tenant. */
export const ADP_DEFAULT_TENANT_KEY = ''
/** The `companyCodeByTenant` catch-all key. */
export const ADP_WILDCARD_TENANT_KEY = '*'

/** ADP Co Codes are 3 characters. Anything else is a config error. */
export const ADP_CO_CODE_LENGTH = 3
/** ADP Batch IDs are short and alphanumeric; longer ones get rejected on import. */
export const ADP_BATCH_ID_MAX_LENGTH = 8

export const ADP_LINE_ENDING: Record<AdpMappingProfile['lineEnding'], string> = {
  crlf: '\r\n',
  lf: '\n',
}

/** The slot ordinals available for a given slot COUNT, e.g. 2 -> [3, 4]. */
export function slotOrdinalRange(count: number): number[] {
  const n = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  return Array.from({ length: n }, (_v, i) => ADP_FIRST_SLOT_ORDINAL + i)
}

/** Is `ordinal` a legal slot for a profile declaring `count` slots? */
export function isSlotOrdinalInRange(ordinal: number, count: number): boolean {
  return (
    Number.isInteger(ordinal) &&
    ordinal >= ADP_FIRST_SLOT_ORDINAL &&
    ordinal <= ADP_FIRST_SLOT_ORDINAL + Math.max(0, Math.floor(count)) - 1
  )
}

// ─── Co Code resolution ────────────────────────────────────────────────────────

/**
 * `PayrollBatchLine.payrollTenant` -> the ADP Co Code that must appear in the
 * file. A File # is only unique WITHIN a Co Code, which is exactly why
 * `partitionPayrollBatch.ts` splits the batch per tenant before serializing.
 *
 * Resolution order: exact key -> case-insensitive key -> `'*'` wildcard -> null.
 * A null/blank tenant looks up `ADP_DEFAULT_TENANT_KEY` ('') first. Returns null
 * when nothing matches — the caller MUST treat that as a blocker, never as an
 * empty cell, or the whole file lands in the wrong company.
 */
export function resolveCoCode(
  profile: AdpMappingProfile,
  tenant: string | null | undefined,
): string | null {
  const map = profile.companyCodeByTenant ?? {}
  const key = (tenant ?? '').trim()
  const pick = (k: string): string | null => {
    const v = map[k]
    return typeof v === 'string' && v.trim() ? v.trim() : null
  }
  if (key) {
    const exact = pick(key)
    if (exact) return exact
    const lower = key.toLowerCase()
    for (const k of Object.keys(map)) {
      if (k.toLowerCase() === lower) {
        const v = pick(k)
        if (v) return v
      }
    }
  } else {
    const dflt = pick(ADP_DEFAULT_TENANT_KEY)
    if (dflt) return dflt
  }
  return pick(ADP_WILDCARD_TENANT_KEY)
}

// ─── Batch ID rendering ────────────────────────────────────────────────────────

/** Just enough of a pay period to render a batch id. A `PayrollPeriod` fits. */
export interface AdpBatchPeriod {
  /** ISO date, inclusive. */
  start: string
  /** ISO date, inclusive. */
  end: string
  label?: string
}

/** Optional extras a template can reference. */
export interface AdpBatchIdContext {
  /** The resolved ADP Co Code, for `{CO_CODE}`. */
  coCode?: string | null
  /** The raw payroll tenant, for `{TENANT}`. */
  tenant?: string | null
  /** A run/spill sequence number, for `{SEQ}`. */
  sequence?: number | string | null
}

const BATCH_ID_TOKENS = [
  'PERIOD_END',
  'PERIOD_START',
  'PERIOD_LABEL',
  'CO_CODE',
  'TENANT',
  'SEQ',
] as const

const TOKEN_RE = /\{([A-Z_]+)(?::([^}]*))?\}/g

/**
 * Format an ISO `YYYY-MM-DD` with a pattern built from YYYY / YY / MM / DD and
 * any literal separators, e.g. 'MMDD' -> '0131', 'MM/DD/YYYY' -> '01/31/2026'.
 * Pure string math — no `Date`, so no timezone can shift the day.
 */
function formatIsoDate(iso: string, fmt: string): string {
  const trimmed = (iso ?? '').trim()
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed)
  if (!m) return trimmed
  const y = m[1] ?? ''
  const mo = m[2] ?? ''
  const d = m[3] ?? ''
  return fmt
    .replace(/YYYY/g, y)
    .replace(/YY/g, y.slice(2))
    .replace(/MM/g, mo)
    .replace(/DD/g, d)
}

/**
 * Render `batchIdTemplate` for a period.
 *
 * Tokens: `{PERIOD_END}` `{PERIOD_START}` (ISO by default, or `{PERIOD_END:MMDD}`
 * style with a YYYY/YY/MM/DD pattern), `{PERIOD_LABEL}`, `{CO_CODE}`, `{TENANT}`,
 * `{SEQ}`. An UNKNOWN token is left in the output verbatim rather than silently
 * dropped, so a typo is visible in the file instead of producing a plausible
 * wrong batch id. `validateAdpProfile` flags unknown tokens at config time.
 */
export function renderBatchId(
  template: string,
  period: AdpBatchPeriod,
  context: AdpBatchIdContext = {},
): string {
  if (!template) return ''
  return template.replace(TOKEN_RE, (whole, token: string, fmt?: string) => {
    switch (token) {
      case 'PERIOD_END':
        return fmt ? formatIsoDate(period.end, fmt) : (period.end ?? '')
      case 'PERIOD_START':
        return fmt ? formatIsoDate(period.start, fmt) : (period.start ?? '')
      case 'PERIOD_LABEL':
        return period.label ?? ''
      case 'CO_CODE':
        return context.coCode ?? ''
      case 'TENANT':
        return context.tenant ?? ''
      case 'SEQ':
        return context.sequence == null ? '' : String(context.sequence)
      default:
        return whole
    }
  })
}

// ─── Earnings code mapping ─────────────────────────────────────────────────────

/**
 * Internal earnings code -> the client's ADP earnings code. Exact match first,
 * then case-insensitive. Returns null when the code is unmapped OR mapped to a
 * blank — both are configuration holes, and the serializer must BLOCK on them
 * rather than emit an unmapped code (ADP silently drops unknown codes, so the
 * worker just quietly does not get paid the premium).
 */
export function mapEarningsCode(
  profile: AdpMappingProfile,
  internalCode: string,
): string | null {
  const map = profile.earningsCodeMap ?? {}
  const key = (internalCode ?? '').trim()
  if (!key) return null
  const direct = map[key]
  if (typeof direct === 'string' && direct.trim()) return direct.trim()
  const lower = key.toLowerCase()
  for (const k of Object.keys(map)) {
    if (k.toLowerCase() === lower) {
      const v = map[k]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
  }
  return null
}

/**
 * Same as `mapEarningsCode`, with the `earnings_codes.hcm_code` column as the
 * fallback.
 *
 * PRECEDENCE (decided in Phase 4, mirror it everywhere): the PROFILE wins. The
 * catalog's `hcm_code` is a single global value, but a customer running several
 * ADP tenancies can need a different ADP code per company — that is exactly what
 * the profile is for. So: profile map -> catalog `hcm_code` -> null (block).
 */
export function mapEarningsCodeWithFallback(
  profile: AdpMappingProfile,
  internalCode: string,
  hcmCode?: string | null,
): string | null {
  const fromProfile = mapEarningsCode(profile, internalCode)
  if (fromProfile) return fromProfile
  return typeof hcmCode === 'string' && hcmCode.trim() ? hcmCode.trim() : null
}

// ─── Cell formatting ───────────────────────────────────────────────────────────

/**
 * Render one cell per its declared format. Null/undefined/blank -> the profile's
 * `emptyValue`. Non-numeric input to a numeric format -> `emptyValue` (never
 * 'NaN', which ADP would reject the whole row over).
 */
export function formatAdpCell(
  value: string | number | null | undefined,
  format: AdpColumnFormat | undefined,
  emptyValue: string,
): string {
  if (value == null) return emptyValue
  if (typeof value === 'string' && !value.trim()) return emptyValue
  switch (format) {
    case 'hours2':
    case 'money2': {
      const n = typeof value === 'number' ? value : Number(String(value).trim())
      if (!Number.isFinite(n)) return emptyValue
      return n.toFixed(2)
    }
    case 'date_mmddyyyy': {
      const s = String(value).trim()
      return /^\d{4}-\d{2}-\d{2}/.test(s) ? formatIsoDate(s, 'MM/DD/YYYY') : s
    }
    case 'text':
    default:
      return String(value).trim()
  }
}

// ─── Validation ────────────────────────────────────────────────────────────────

const BOM = '﻿'

/** Case/punctuation-insensitive header key: 'O/T Hours' -> 'othours'. */
function normalizeHeaderKey(s: string): string {
  return (s ?? '')
    .split(BOM)
    .join('')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
}

/** Slot ordinal a column binds to, or null when it is not a slot column. */
function slotOrdinalOf(
  source: AdpColumnSource,
): { ordinal: number; family: 'hours' | 'earnings' } | null {
  switch (source.kind) {
    case 'hoursSlotCode':
    case 'hoursSlotAmount':
      return { ordinal: source.slot, family: 'hours' }
    case 'earningsSlotCode':
    case 'earningsSlotAmount':
      return { ordinal: source.slot, family: 'earnings' }
    default:
      return null
  }
}

/**
 * Config-time problems, as human-readable sentences (empty array = clean).
 *
 * Catches, at minimum: duplicate column names, a slot ordinal beyond the
 * declared slot count, an empty `earningsCodeMap` entry, a tenant with no Co
 * Code, and a `regularCodes`/`overtimeCodes` code that is ALSO pinned into a
 * dynamic slot (which double-pays it — once in `Reg Hours`, once in `Hours 3`).
 *
 * Advisory items (Co Code length, Batch ID length) are in the same list on
 * purpose: every one of them is something ADP's own importer rejects.
 */
export function validateAdpProfile(profile: AdpMappingProfile): string[] {
  if (profile.vendorFormat === 'adp_epip') return validateEpipProfile(profile)
  const problems: string[] = []
  const columns = profile.columns ?? []

  if (!profile.id?.trim()) problems.push('Profile id is blank.')
  if (!profile.name?.trim()) problems.push('Profile name is blank.')
  if (columns.length === 0) problems.push('Profile has no columns, so the file would be empty.')

  // Duplicate column headers.
  const seenHeader = new Map<string, string>()
  for (const c of columns) {
    const raw = (c.column ?? '').trim()
    if (!raw) {
      problems.push('A column has a blank header name.')
      continue
    }
    const key = normalizeHeaderKey(raw)
    const prior = seenHeader.get(key)
    if (prior) problems.push(`Duplicate column name: "${prior}" and "${raw}" collide.`)
    else seenHeader.set(key, raw)
  }

  // Slot ordinals in range, and Code/Amount paired.
  const hoursCode = new Set<number>()
  const hoursAmt = new Set<number>()
  const earnCode = new Set<number>()
  const earnAmt = new Set<number>()
  for (const c of columns) {
    const slot = slotOrdinalOf(c.source)
    if (!slot) continue
    const count = slot.family === 'hours' ? profile.hoursSlots : profile.earningsSlots
    const label = slot.family === 'hours' ? 'hoursSlots' : 'earningsSlots'
    if (!isSlotOrdinalInRange(slot.ordinal, count)) {
      const range = slotOrdinalRange(count)
      problems.push(
        `Column "${c.column}" references ${slot.family} slot ${slot.ordinal}, but ${label} is ${count} ` +
          `(valid slots: ${range.length ? range.join(', ') : 'none'}).`,
      )
    }
    if (c.source.kind === 'hoursSlotCode') hoursCode.add(slot.ordinal)
    if (c.source.kind === 'hoursSlotAmount') hoursAmt.add(slot.ordinal)
    if (c.source.kind === 'earningsSlotCode') earnCode.add(slot.ordinal)
    if (c.source.kind === 'earningsSlotAmount') earnAmt.add(slot.ordinal)
  }
  for (const o of hoursCode) {
    if (!hoursAmt.has(o)) problems.push(`Hours slot ${o} has a Code column but no Amount column.`)
  }
  for (const o of hoursAmt) {
    if (!hoursCode.has(o)) problems.push(`Hours slot ${o} has an Amount column but no Code column.`)
  }
  for (const o of earnCode) {
    if (!earnAmt.has(o)) problems.push(`Earnings slot ${o} has a Code column but no Amount column.`)
  }
  for (const o of earnAmt) {
    if (!earnCode.has(o)) problems.push(`Earnings slot ${o} has an Amount column but no Code column.`)
  }

  // Co Code map — only meaningful when a column actually emits the Co Code.
  const bindsCoCode = columns.some(
    (c) => c.source.kind === 'field' && c.source.field === 'coCode',
  )
  const tenantKeys = Object.keys(profile.companyCodeByTenant ?? {})
  if (tenantKeys.length === 0 && bindsCoCode) {
    problems.push('No Co Code is configured for any payroll tenant, so every worker would block.')
  }
  for (const k of tenantKeys) {
    const v = profile.companyCodeByTenant[k]
    const shown = k === ADP_DEFAULT_TENANT_KEY ? '(untagged / single-tenant)' : `"${k}"`
    if (typeof v !== 'string' || !v.trim()) {
      problems.push(`Payroll tenant ${shown} has no Co Code.`)
    } else if (v.trim().length !== ADP_CO_CODE_LENGTH) {
      problems.push(
        `Co Code "${v.trim()}" for tenant ${shown} is ${v.trim().length} characters; ` +
          `ADP Co Codes are ${ADP_CO_CODE_LENGTH}.`,
      )
    }
  }

  // Earnings code map.
  for (const [k, v] of Object.entries(profile.earningsCodeMap ?? {})) {
    if (!k.trim()) problems.push('An earnings code mapping has a blank internal code.')
    if (typeof v !== 'string' || !v.trim()) {
      problems.push(`Internal earnings code "${k}" is mapped to a blank ADP code.`)
    }
  }

  // REG/OT must have a home column and must not ALSO be pinned into a slot.
  // The only way a code gets pinned into a slot in this shape is a literal Code
  // column sitting where a dynamic slot code would otherwise go.
  const slotPinnedCodes = new Set<string>()
  for (const c of columns) {
    if (c.source.kind !== 'literal') continue
    const key = normalizeHeaderKey(c.column)
    if (/^(?:hours|hrs|earnings|earning|earn)\d+code$/.test(key)) {
      slotPinnedCodes.add(c.source.value.trim().toUpperCase())
    }
  }
  const fixedCodeColumns = new Map<string, string[]>()
  for (const c of columns) {
    if (c.source.kind !== 'earningsHours' && c.source.kind !== 'earningsAmount') continue
    for (const code of c.source.earningsCodes ?? []) {
      const upper = code.trim().toUpperCase()
      const arr = fixedCodeColumns.get(upper) ?? []
      arr.push(c.column)
      fixedCodeColumns.set(upper, arr)
    }
  }
  for (const group of [
    { label: 'regularCodes', codes: profile.regularCodes ?? [] },
    { label: 'overtimeCodes', codes: profile.overtimeCodes ?? [] },
  ]) {
    for (const raw of group.codes) {
      const code = raw.trim().toUpperCase()
      if (!code) {
        problems.push(`${group.label} contains a blank code.`)
        continue
      }
      if (slotPinnedCodes.has(code)) {
        problems.push(
          `Earnings code "${raw}" is in ${group.label} AND pinned to a slot column, ` +
            `so it would be paid twice.`,
        )
      }
      if (!fixedCodeColumns.has(code)) {
        problems.push(
          `Earnings code "${raw}" is in ${group.label} but no column carries it, ` +
            `so those hours would silently vanish.`,
        )
      }
    }
  }
  const regSet = new Set((profile.regularCodes ?? []).map((c) => c.trim().toUpperCase()))
  for (const raw of profile.overtimeCodes ?? []) {
    if (regSet.has(raw.trim().toUpperCase())) {
      problems.push(`Earnings code "${raw}" is in both regularCodes and overtimeCodes.`)
    }
  }

  // Identity columns WFN cannot import without.
  if (profile.vendorFormat === 'wfn_paydata_csv') {
    const fields = new Set<AdpMappableField>()
    for (const c of columns) {
      if (c.source.kind === 'field') fields.add(c.source.field)
    }
    for (const required of ['coCode', 'fileNumber'] as AdpMappableField[]) {
      if (!fields.has(required)) {
        problems.push(`WFN paydata requires a column bound to "${required}"; none is mapped.`)
      }
    }
    if (profile.hoursSlots <= 0) {
      problems.push(
        'No Hours slots are configured, so shift/night/holiday premium hours have nowhere to go.',
      )
    }
  }

  // Batch ID template.
  const unknownTokens = new Set<string>()
  for (const m of (profile.batchIdTemplate ?? '').matchAll(TOKEN_RE)) {
    const token = m[1] ?? ''
    if (token && !(BATCH_ID_TOKENS as readonly string[]).includes(token)) unknownTokens.add(token)
  }
  for (const t of unknownTokens) {
    problems.push(
      `Batch ID template uses unknown token {${t}} (known: ${BATCH_ID_TOKENS.join(', ')}).`,
    )
  }
  if (profile.batchIdTemplate?.trim()) {
    const probe = renderBatchId(
      profile.batchIdTemplate,
      { start: '2026-01-01', end: '2026-01-31', label: 'PP-2026-01' },
      { coCode: 'ABC', tenant: 'ABC', sequence: 1 },
    )
    if (probe.length > ADP_BATCH_ID_MAX_LENGTH) {
      problems.push(
        `Batch ID template renders ${probe.length} characters ("${probe}"); ` +
          `ADP may reject anything over ${ADP_BATCH_ID_MAX_LENGTH}.`,
      )
    }
  }

  if (!Number.isInteger(profile.hoursSlots) || profile.hoursSlots < 0) {
    problems.push('hoursSlots must be a non-negative whole number.')
  }
  if (!Number.isInteger(profile.earningsSlots) || profile.earningsSlots < 0) {
    problems.push('earningsSlots must be a non-negative whole number.')
  }

  return problems
}

/**
 * The EPIP half of `validateAdpProfile`. An EPIP profile has no column grid, so
 * the grid checks do not apply; what does apply is the layout itself, the Co
 * Code map (the HDR record's company code) and the earnings code map (every
 * DET record needs an ADP earning code, there is no fixed Reg Hours column to
 * fall back on).
 */
function validateEpipProfile(profile: AdpMappingProfile): string[] {
  const problems: string[] = []
  if (!profile.id?.trim()) problems.push('Profile id is blank.')
  if (!profile.name?.trim()) problems.push('Profile name is blank.')
  if (!profile.epip) {
    problems.push('The profile is an EPIP profile but carries no EPIP layout.')
    return problems
  }
  const layout = normalizeEpipLayout(profile.epip)
  problems.push(...validateEpipLayout(layout))

  // The ADP-code maps only matter when the profile translates to ADP codes.
  // With SAP identifiers (the Codan spec) the company code is the SAP company
  // code and the earnings code is the S/4-side code, so neither map is read.
  if (layout.companyCodeSource === 'adp_map') {
    const tenantKeys = Object.keys(profile.companyCodeByTenant ?? {})
    if (tenantKeys.length === 0) {
      problems.push('No ADP company code is configured for any payroll tenant, so every worker would block.')
    }
    for (const k of tenantKeys) {
      const v = profile.companyCodeByTenant[k]
      const shown = k === ADP_DEFAULT_TENANT_KEY ? '(untagged / single-tenant)' : `"${k}"`
      if (typeof v !== 'string' || !v.trim()) problems.push(`Payroll tenant ${shown} has no ADP company code.`)
    }
  }
  if (layout.earningsCodeSource === 'adp_map') {
    for (const [k, v] of Object.entries(profile.earningsCodeMap ?? {})) {
      if (!k.trim()) problems.push('An earnings code mapping has a blank internal code.')
      if (typeof v !== 'string' || !v.trim()) {
        problems.push(`Internal earnings code "${k}" is mapped to a blank ADP code.`)
      }
    }
    if (Object.keys(profile.earningsCodeMap ?? {}).length === 0) {
      problems.push('The earnings code map is empty. Every detail record needs an ADP earning code, so only codes with an hcm_code on the catalog will resolve.')
    }
  }
  if (layout.fileNumberSource === 'crosswalk' && layout.companyCodeSource === 'sap_company_code') {
    problems.push('The employee number comes from the ADP crosswalk but the company code is the SAP company code. Mixing ADP and SAP identifiers in one batch is almost always a mistake.')
  }
  return problems
}

// ─── Pasted-header re-bind ─────────────────────────────────────────────────────

/**
 * Split one delimited line, honouring `"` quoting and `""` escapes. Handles both
 * comma (a saved CSV) and tab (an Excel/WFN grid paste).
 */
function splitDelimitedLine(line: string, delimiter: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line.charAt(i)
    if (inQuotes) {
      if (ch === '"') {
        if (line.charAt(i + 1) === '"') {
          cur += '"'
          i++
        } else inQuotes = false
      } else cur += ch
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === delimiter) {
      out.push(cur)
      cur = ''
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out.map((c) => c.trim())
}

/** Tab wins when present — that is what an Excel/WFN grid paste looks like. */
function detectDelimiter(line: string): string {
  if (line.includes('\t')) return '\t'
  if (line.includes(';') && !line.includes(',')) return ';'
  return ','
}

const FIELD_ALIASES: Record<string, { field: AdpMappableField; format: AdpColumnFormat }> = {
  cocode: { field: 'coCode', format: 'text' },
  co: { field: 'coCode', format: 'text' },
  company: { field: 'coCode', format: 'text' },
  companycode: { field: 'coCode', format: 'text' },
  batchid: { field: 'batchId', format: 'text' },
  batch: { field: 'batchId', format: 'text' },
  batchno: { field: 'batchId', format: 'text' },
  batchnumber: { field: 'batchId', format: 'text' },
  file: { field: 'fileNumber', format: 'text' },
  fileno: { field: 'fileNumber', format: 'text' },
  filenum: { field: 'fileNumber', format: 'text' },
  filenumber: { field: 'fileNumber', format: 'text' },
  associateid: { field: 'fileNumber', format: 'text' },
  associd: { field: 'fileNumber', format: 'text' },
  employeeid: { field: 'fileNumber', format: 'text' },
  empid: { field: 'fileNumber', format: 'text' },
  workdate: { field: 'workDate', format: 'date_mmddyyyy' },
  date: { field: 'workDate', format: 'date_mmddyyyy' },
  periodend: { field: 'periodEnd', format: 'date_mmddyyyy' },
  periodending: { field: 'periodEnd', format: 'date_mmddyyyy' },
  payperiodend: { field: 'periodEnd', format: 'date_mmddyyyy' },
  payperiodending: { field: 'periodEnd', format: 'date_mmddyyyy' },
  weekending: { field: 'periodEnd', format: 'date_mmddyyyy' },
  employeename: { field: 'employeeName', format: 'text' },
  associatename: { field: 'employeeName', format: 'text' },
  workername: { field: 'employeeName', format: 'text' },
  name: { field: 'employeeName', format: 'text' },
  tempdept: { field: 'tempDept', format: 'text' },
  tempdepartment: { field: 'tempDept', format: 'text' },
  temporarydepartment: { field: 'tempDept', format: 'text' },
  temprate: { field: 'tempRate', format: 'money2' },
  temporaryrate: { field: 'tempRate', format: 'money2' },
  positionid: { field: 'positionId', format: 'text' },
  position: { field: 'positionId', format: 'text' },
  jobnumber: { field: 'positionId', format: 'text' },
  payrolltenant: { field: 'payrollTenant', format: 'text' },
  tenant: { field: 'payrollTenant', format: 'text' },
}

/**
 * Best-effort binding for a header the base profile does not already know.
 * Recognises the standard WFN vocabulary plus the common client renames.
 * Exported so the Phase 5 profile editor can show "we think this means X".
 */
export function inferAdpColumnSource(
  header: string,
  profile: Pick<AdpMappingProfile, 'regularCodes' | 'overtimeCodes'>,
): { source: AdpColumnSource; format: AdpColumnFormat } | null {
  const k = normalizeHeaderKey(header)
  if (!k) return null

  const hoursSlot = /^(?:hours|hrs)(\d+)(code|amount|amt|hours|hrs)$/.exec(k)
  if (hoursSlot) {
    const ordinal = Number(hoursSlot[1])
    return hoursSlot[2] === 'code'
      ? { source: { kind: 'hoursSlotCode', slot: ordinal }, format: 'text' }
      : { source: { kind: 'hoursSlotAmount', slot: ordinal }, format: 'hours2' }
  }
  const earnSlot = /^(?:earnings|earning|earn)(\d+)(code|amount|amt)$/.exec(k)
  if (earnSlot) {
    const ordinal = Number(earnSlot[1])
    return earnSlot[2] === 'code'
      ? { source: { kind: 'earningsSlotCode', slot: ordinal }, format: 'text' }
      : { source: { kind: 'earningsSlotAmount', slot: ordinal }, format: 'money2' }
  }

  const f = FIELD_ALIASES[k]
  if (f) return { source: { kind: 'field', field: f.field }, format: f.format }

  if (['reghours', 'regularhours', 'reghrs', 'regularhrs'].includes(k)) {
    return {
      source: { kind: 'earningsHours', earningsCodes: [...(profile.regularCodes ?? [])] },
      format: 'hours2',
    }
  }
  if (['othours', 'overtimehours', 'othrs', 'overtimehrs'].includes(k)) {
    return {
      source: { kind: 'earningsHours', earningsCodes: [...(profile.overtimeCodes ?? [])] },
      format: 'hours2',
    }
  }
  // Reg/OT Earnings are DOLLAR overrides that cancel the matching Hours column.
  // Recognised so the header lines up, deliberately bound to nothing.
  if (['regearnings', 'regularearnings', 'regearn'].includes(k)) {
    return { source: { kind: 'blank' }, format: 'money2' }
  }
  if (['otearnings', 'overtimeearnings'].includes(k)) {
    return { source: { kind: 'blank' }, format: 'money2' }
  }
  if (k === 'memocode') return { source: { kind: 'blank' }, format: 'text' }
  if (['memoamount', 'memoamt'].includes(k)) return { source: { kind: 'blank' }, format: 'money2' }

  return null
}

/**
 * Rebuild `columns` from a header row the client pasted out of THEIR OWN WFN
 * paydata template.
 *
 * WFN paydata headers are configured per company, so the shipped preset is only
 * a starting point — this paste path is the difference between "works" and "ADP
 * rejects the file". Known headers keep their existing source binding (matched
 * on a punctuation/case-insensitive form of the name) but adopt the client's
 * exact header TEXT, so the emitted file is byte-identical to their template.
 * Unrecognised headers are kept as positional `{ kind: 'blank' }` columns — the
 * column stays in the file (so the layout still lines up) but carries nothing.
 *
 * `hoursSlots` / `earningsSlots` are re-derived from the highest slot ordinal
 * actually present, so a client template with `Hours 3..6` yields `hoursSlots: 4`.
 * Pure: returns a NEW profile, never mutates the input.
 */
export function parseAdpHeaderRow(
  pastedText: string,
  profile: AdpMappingProfile,
): AdpMappingProfile {
  const firstLine = (pastedText ?? '')
    .split(BOM)
    .join('')
    .split(/\r\n|\n|\r/)
    .find((l) => l.trim().length > 0)
  if (!firstLine) return { ...profile, columns: [...(profile.columns ?? [])] }

  const cells = splitDelimitedLine(firstLine, detectDelimiter(firstLine))
  // Tolerate a single trailing delimiter (Excel does this constantly).
  const headers = cells.length > 1 && cells[cells.length - 1] === '' ? cells.slice(0, -1) : cells

  const known = new Map<string, AdpColumnMapping>()
  for (const c of profile.columns ?? []) {
    const key = normalizeHeaderKey(c.column)
    if (key && !known.has(key)) known.set(key, c)
  }

  const columns: AdpColumnMapping[] = headers.map((header) => {
    const key = normalizeHeaderKey(header)
    const prior = known.get(key)
    if (prior) return { column: header, source: prior.source, format: prior.format }
    const inferred = inferAdpColumnSource(header, profile)
    if (inferred) return { column: header, source: inferred.source, format: inferred.format }
    return { column: header, source: { kind: 'blank' }, format: 'text' }
  })

  let maxHours = ADP_FIRST_SLOT_ORDINAL - 1
  let maxEarnings = ADP_FIRST_SLOT_ORDINAL - 1
  for (const c of columns) {
    const slot = slotOrdinalOf(c.source)
    if (!slot) continue
    if (slot.family === 'hours') maxHours = Math.max(maxHours, slot.ordinal)
    else maxEarnings = Math.max(maxEarnings, slot.ordinal)
  }

  return {
    ...profile,
    columns,
    hoursSlots: Math.max(0, maxHours - ADP_FIRST_SLOT_ORDINAL + 1),
    earningsSlots: Math.max(0, maxEarnings - ADP_FIRST_SLOT_ORDINAL + 1),
  }
}

/** The header row a profile emits, in order. Handy for a preview + tests. */
export function adpHeaderRow(profile: AdpMappingProfile): string[] {
  return (profile.columns ?? []).map((c) => c.column)
}
